// ============================================================
// CF.crud — Reusable CRUD modal flow helper
// ============================================================
// Eliminates copy-paste between submitCreate / submitEdit /
// confirmDelete / loadById in Setting pages by encapsulating
// the common pattern:
//
//   1. (optional) Read & validate fields
//   2. (optional) Show CF.confirm dialog
//   3. CF.formHelpers.setSubmitting(btnId, true)
//   4. CF.auth.api({url, method, contentType, data})
//   5. on success: CF.toast.success + CF.modal.close + table.reload
//   6. on error:   CF.formHelpers.handleApiError + toast
//   7. always:     CF.formHelpers.setSubmitting(btnId, false)
//
// All functions are pure additive helpers — they do NOT change
// existing API contracts and can be adopted page-by-page.
// ============================================================

const CF = window.CF = window.CF || {};

/**
 * Submit a form to a JSON endpoint and handle the standard response flow.
 *
 * @param {Object}   opts
 * @param {string}   opts.url           - API endpoint
 * @param {string}   [opts.method=POST] - HTTP method (POST / PUT / PATCH / DELETE)
 * @param {Object}   opts.body          - Request body (will be JSON.stringify'd)
 * @param {string}   opts.btnId         - Element id of the submit button
 * @param {string}   [opts.modalId]     - Modal id to close on success
 * @param {Object}   [opts.table]       - DataTable instance to reload on success
 * @param {Object}   [opts.tableReload] - Reload args for table.reload() (default: { page: 1 } for create, no args for update)
 * @param {string}   [opts.successMsg]  - Toast message on success (falls back to res.message)
 * @param {Object}   [opts.confirm]     - Pass-through for CF.confirm({...}); set to false to skip
 * @param {Function} [opts.onSuccess]   - Extra callback after default success handling
 * @returns {Promise<boolean>} resolves true if request succeeded, false otherwise (or aborted)
 */
export function submit(opts) {
    const method = (opts.method || 'POST').toUpperCase();
    const reloadArgs = opts.tableReload !== undefined
        ? opts.tableReload
        : (method === 'POST' ? { page: 1 } : undefined);

    const proceed = function () {
        if (opts.btnId) CF.formHelpers.setSubmitting(opts.btnId, true);

        return CF.auth.api({
            url:         opts.url,
            method:      method,
            contentType: 'application/json',
            data:        JSON.stringify(opts.body || {})
        }).then(function (res) {
            if (opts.btnId) CF.formHelpers.setSubmitting(opts.btnId, false);

            if (res && res.success) {
                if (opts.successMsg || (res && res.message)) {
                    CF.toast.success(opts.successMsg || res.message);
                }
                if (opts.modalId) CF.modal.close(opts.modalId);
                if (opts.table)   opts.table.reload(reloadArgs);
                if (typeof opts.onSuccess === 'function') opts.onSuccess(res);
                return true;
            }
            CF.toast.error((res && res.message) || 'Operation failed.');
            return false;
        }).fail(function (xhr) {
            if (opts.btnId) CF.formHelpers.setSubmitting(opts.btnId, false);
            CF.formHelpers.handleApiError(xhr);
            return false;
        });
    };

    if (opts.confirm === false || !opts.confirm) {
        return Promise.resolve().then(proceed);
    }

    return CF.confirm(opts.confirm).then(function (confirmed) {
        if (!confirmed) return false;
        return proceed();
    });
}

/**
 * GET a single resource and run a populate callback with the data.
 *
 * @param {Object}   opts
 * @param {string}   opts.url
 * @param {string}   [opts.notFoundMsg='Record not found.']
 * @param {string}   [opts.errorMsg='Failed to load record.']
 * @param {Function} opts.populate     - (data) => void
 * @param {Function} [opts.onSuccess]  - (res) => void (after populate)
 * @returns {Promise}
 */
export function loadById(opts) {
    return CF.auth.api({ url: opts.url, method: 'GET' })
        .then(function (res) {
            if (!res || !res.success || !res.data) {
                CF.toast.error((res && res.message) || opts.notFoundMsg || 'Record not found.');
                return null;
            }
            if (typeof opts.populate === 'function') opts.populate(res.data);
            if (typeof opts.onSuccess === 'function') opts.onSuccess(res);
            return res.data;
        }).fail(function () {
            CF.toast.error(opts.errorMsg || 'Failed to load record.');
        });
}

/**
 * Show a delete confirmation, then DELETE and reload the table.
 *
 * @param {Object} opts
 * @param {string} opts.url
 * @param {Object} [opts.table]
 * @param {string} [opts.title='Delete?']
 * @param {string} [opts.message]
 * @param {string} [opts.successMsg='Record deleted.']
 * @returns {Promise<boolean>}
 */
export function confirmDelete(opts) {
    return CF.confirm({
        type:        'danger',
        title:       opts.title   || 'Delete?',
        message:     opts.message || 'This action cannot be undone.',
        confirmText: opts.confirmText || 'Delete',
        cancelText:  opts.cancelText  || 'Cancel'
    }).then(function (confirmed) {
        if (!confirmed) return false;
        return CF.auth.api({ url: opts.url, method: 'DELETE' })
            .then(function (res) {
                if (res && res.success) {
                    CF.toast.success(opts.successMsg || res.message || 'Record deleted.');
                    if (opts.table) opts.table.reload();
                    return true;
                }
                CF.toast.error((res && res.message) || 'Delete failed.');
                return false;
            }).fail(function (xhr) {
                CF.formHelpers.handleApiError(xhr);
                return false;
            });
    });
}

// Expose on window.CF for legacy page scripts
CF.crud = { submit, loadById, confirmDelete };
